import React, { useMemo } from 'react';
import {
  Chip,
  Divider,
  TextField,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import Autocomplete from '@mui/material/Autocomplete';
import { parseEntityRef } from '@backstage/catalog-model';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { useQuery } from '@tanstack/react-query';
import type { RowState } from '@internal/backstage-plugin-bidone-backend';
import { useBDirectRows } from '../../../hooks/useBDirectRows';

// Filter chip identity. Three real states plus a virtual `non_repo` chip:
// non-repo rows don't carry a meaningful `state` (states only make sense
// for the repo-backed checklist rows that pull live ADO/Azure data), so
// they get their own chip rather than being lumped under `not_ready`.
export type FilterChip = RowState | 'non_repo';

// Frontend-local list; the backend's `ROW_STATES` runtime constant is
// deliberately not re-exported from the barrel (it would drag the backend's
// `crypto` runtime into the bundle — see `api/index.ts`).
const FILTER_CHIPS: FilterChip[] = [
  'not_ready',
  'no_changes',
  'tagged',
  'non_repo',
];

const CHIP_LABEL: Record<FilterChip, string> = {
  not_ready: 'Not ready',
  no_changes: 'No changes',
  tagged: 'Tagged',
  non_repo: 'Non-repo',
};

const Wrapper = styled('div')(({ theme }) => ({
  marginTop: theme.spacing(2),
}));

const FiltersDivider = styled(Divider)(({ theme }) => ({
  marginBottom: theme.spacing(2),
}));

const Heading = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(1),
}));

const Field = styled('div')(({ theme }) => ({
  width: '100%',
  marginBottom: theme.spacing(2),
  // Match the State dropdown's compact trigger (see BDirectSectionCard
  // styles). Font size goes on the InputBase root; the chevron/clear
  // padding is left to MUI so the icons stay clear of the input text.
  // The v5- prefix is the classname Backstage's createUnifiedTheme installs
  // via ClassNameGenerator to keep v4 and v5 utility classes from colliding.
  '& [class*="v5-MuiInputBase-root"]': {
    fontSize: '0.8rem',
  },
}));

const FieldLabel = styled(Typography)(({ theme }) => ({
  display: 'block',
  marginBottom: theme.spacing(0.5),
  color: theme.palette.text.secondary,
}));

const ChipRow = styled('div')(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.spacing(0.75),
}));

// Count is rendered as a trailing pill inside the chip label — using a real
// <Badge> overlays the chip border at awkward angles, so we keep it inline.
const Count = styled('span')(({ theme }) => ({
  marginLeft: theme.spacing(0.75),
  padding: '0 6px',
  borderRadius: 10,
  backgroundColor: 'rgba(0, 0, 0, 0.12)',
  fontSize: '0.7rem',
  minWidth: 18,
  textAlign: 'center',
  display: 'inline-block',
}));

// Shared chip baseline + per-state selected colour. `selectedKind` is the
// FilterChip when selected, undefined when not — drives bg/text colour off
// the theme palette without dragging the conditional into JSX.
// Non-repo doesn't map to a state colour — we use info.light so it reads as
// "different category" rather than another state.
type SelectedKind = FilterChip | undefined;
const SELECTED_PALETTE: Record<FilterChip, 'warning' | 'success' | 'info' | 'action'> = {
  not_ready: 'warning',
  tagged: 'success',
  non_repo: 'info',
  no_changes: 'action',
};

const StateChip = styled(Chip, {
  shouldForwardProp: prop => prop !== 'selectedKind',
})<{ selectedKind: SelectedKind }>(({ theme, selectedKind }) => {
  const base = {
    fontWeight: 500,
    '&:focus': { outline: 'none' },
  } as const;
  if (!selectedKind) return base;
  const tone = SELECTED_PALETTE[selectedKind];
  if (tone === 'action') {
    return {
      ...base,
      backgroundColor: theme.palette.action.disabledBackground,
    };
  }
  return {
    ...base,
    backgroundColor: theme.palette[tone].light,
    color: theme.palette[tone].contrastText,
    '&:hover, &:focus': { backgroundColor: theme.palette[tone].light },
  };
});

interface Props {
  release: string | null;
  ownerFilter: string | null;
  setOwnerFilter: (ref: string | null) => void;
  stateFilter: Set<FilterChip>;
  setStateFilter: (next: Set<FilterChip>) => void;
}

// Fallback when the catalog hasn't resolved a display name for an owner ref:
// pull the readable `name` out of `user:default/jane.doe` → `jane.doe`.
const fallbackOwnerLabel = (ref: string): string => {
  try {
    return parseEntityRef(ref).name;
  } catch {
    return ref;
  }
};

// Pull the friendliest available display name out of a catalog User or Group
// entity. Prefer `spec.profile.displayName` (set by the MS Graph / Okta
// providers), then `metadata.title`, then the raw `metadata.name`.
const displayNameOf = (entity: {
  metadata: { name?: string; title?: string };
  spec?: { profile?: { displayName?: string } };
}): string =>
  entity.spec?.profile?.displayName ??
  entity.metadata.title ??
  entity.metadata.name ??
  '';

export const Filters = ({
  release,
  ownerFilter,
  setOwnerFilter,
  stateFilter,
  setStateFilter,
}: Props) => {
  const catalogApi = useApi(catalogApiRef);
  // `useBDirectRows` does the catalog join that decorates each row with
  // `owner`. React Query keys on `release` so this is a cache hit when
  // `ChecklistDetail` is mounted alongside.
  const { items, env } = useBDirectRows(release);

  // Curated owner list: only refs that actually appear in this release's
  // steps. Showing all catalog Users would surface dozens of irrelevant
  // names — keeping it tight makes the dropdown meaningful.
  const ownerOptions = useMemo<string[]>(() => {
    const seen = new Set<string>();
    for (const i of items) {
      if (i.owner) seen.add(i.owner);
    }
    return Array.from(seen).sort();
  }, [items]);

  // Resolve owner refs → display names so the dropdown shows "Hugh Hoang"
  // instead of "hugh.hoang". Batch lookup keyed on the sorted ref list — same
  // owner set across releases stays cached. Refs absent from the catalog
  // (deleted users) fall back to the parsed `name` part.
  const ownerLabelsKey = ownerOptions.join('|');
  const { data: ownerLabels } = useQuery({
    queryKey: ['release-checklist', 'owner-labels', ownerLabelsKey],
    enabled: ownerOptions.length > 0,
    queryFn: async () => {
      const { items: entities } = await catalogApi.getEntitiesByRefs({
        entityRefs: ownerOptions,
      });
      const map = new Map<string, string>();
      ownerOptions.forEach((ref, i) => {
        const entity = entities[i];
        if (entity) map.set(ref, displayNameOf(entity));
      });
      return map;
    },
  });

  const ownerLabel = (ref: string): string =>
    ownerLabels?.get(ref) || fallbackOwnerLabel(ref);

  // Per-chip totals across the release (steps only). Counts narrow when an
  // owner filter is active — they represent "how many rows would each chip
  // show right now" within the current owner scope. Counts do NOT react to
  // other chip selections; otherwise the badges would shift as the user
  // clicks them, which feels janky. State counts cover repo rows only —
  // non-repo rows have their own chip and shouldn't double-count.
  const chipCounts = useMemo<Record<FilterChip, number>>(() => {
    const counts: Record<FilterChip, number> = {
      not_ready: 0,
      no_changes: 0,
      tagged: 0,
      non_repo: 0,
    };
    for (const item of items) {
      if (item.kind !== 'step') continue;
      if (ownerFilter && item.owner !== ownerFilter) continue;
      if (!item.repo) {
        // Only count non-repo rows that actually render for the current
        // env — the detail page hides rows whose template declares no
        // checkbox in this env (UAT-only step on a `*-prod` release,
        // etc.), so the badge should reflect what's visible.
        const envApplicable = Object.values(item.completed).some(
          envs => envs?.[env] !== undefined,
        );
        if (envApplicable) counts.non_repo += 1;
        continue;
      }
      counts[item.state] += 1;
    }
    return counts;
  }, [items, ownerFilter, env]);

  const toggleChip = (c: FilterChip) => {
    const next = new Set(stateFilter);
    if (next.has(c)) next.delete(c);
    else next.add(c);
    setStateFilter(next);
  };

  // Rendered inline inside the "Manage releases" card — no own Card chrome.
  // The Divider separates filter inputs from the release picker / action row
  // above without breaking the visual containment of the parent card.
  if (!release) return null;
  return (
    <Wrapper>
      <FiltersDivider />
      <Heading variant="h6">Filters</Heading>

      <Field>
        <FieldLabel variant="caption">Owner</FieldLabel>
        <Autocomplete
          size="small"
          options={ownerOptions}
          getOptionLabel={ownerLabel}
          value={ownerFilter}
          onChange={(_, v) => setOwnerFilter(v)}
          renderInput={params => (
            <TextField
              {...params}
              variant="outlined"
              placeholder="Any owner"
            />
          )}
          noOptionsText="No owners in this release"
        />
      </Field>

      <FieldLabel variant="caption">State</FieldLabel>
      <ChipRow>
        {FILTER_CHIPS.map(c => {
          const selected = stateFilter.has(c);
          return (
            <StateChip
              key={c}
              size="small"
              selectedKind={selected ? c : undefined}
              label={
                <>
                  {CHIP_LABEL[c]}
                  <Count>{chipCounts[c]}</Count>
                </>
              }
              onClick={() => toggleChip(c)}
              variant={selected ? 'filled' : 'outlined'}
            />
          );
        })}
      </ChipRow>
    </Wrapper>
  );
};
